import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore'
import toast from 'react-hot-toast'

const Quiz = ({ userData, onBack, selectedLevel }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [gameLoading, setGameLoading] = useState(false)
  const [currentLevel, setCurrentLevel] = useState(selectedLevel)
  const [userLevel, setUserLevel] = useState('A1')
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const [lives, setLives] = useState(3)
  const [score, setScore] = useState(0)
  const [gameCompleted, setGameCompleted] = useState(false)
  const [showExitDialog, setShowExitDialog] = useState(false)
  const [showLevelCompleteDialog, setShowLevelCompleteDialog] = useState(false)
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false)
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false)

  useEffect(() => {
    if (user && userData && selectedLevel) {
      setUserLevel(userData.level || 'A1')
      setCurrentLevel(selectedLevel)
      loadQuestions()
    }
  }, [user, userData, selectedLevel])

  useEffect(() => {
    if (selectedLevel) {
      setCurrentLevel(selectedLevel)
    }
  }, [selectedLevel])

  const loadQuestions = async () => {
    try {
      setLoading(true)
      console.log('Loading quiz questions for level:', userLevel, 'game level:', selectedLevel)

      if (!selectedLevel) {
        console.error('selectedLevel is undefined')
        toast.error('Level seçimi yapılmadı')
        return
      }

      const firestoreLevel = userLevel.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'questions' : 'questions'

      const levelDocRef = doc(db, 'quizzes', firestoreLevel, 'levels', selectedLevel.toString())
      const levelDoc = await getDoc(levelDocRef)

      if (!levelDoc.exists()) {
        toast.error('Bu level için soru bulunamadı')
        return
      }

      const data = levelDoc.data()
      const levelQuestions = data[fieldName] || []

      if (levelQuestions.length === 0) {
        toast.error('Bu level için soru bulunamadı')
        return
      }

      console.log('Level questions:', levelQuestions.length)
      setQuestions(levelQuestions)
      startGame(levelQuestions)
    } catch (error) {
      console.error('Error loading questions:', error)
      toast.error('Sorular yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const startGame = (questionList) => {
    if (!questionList || questionList.length === 0) {
      toast.error('Soru bulunamadı')
      return
    }

    setCurrentQuestionIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setSelectedAnswer('')
    setShowAnswerFeedback(false)
    setIsAnswerCorrect(false)
  }

  const handleAnswerSelect = (answer) => {
    if (gameLoading) return
    setSelectedAnswer(answer)
  }

  const handleSubmit = () => {
    if (gameLoading || !selectedAnswer) return

    setGameLoading(true)
    const currentQuestion = questions[currentQuestionIndex]
    const isCorrect = selectedAnswer === currentQuestion[`option${currentQuestion.correctOption}`]

    // Cevap geri bildirimi göster
    setIsAnswerCorrect(isCorrect)
    setShowAnswerFeedback(true)

    if (isCorrect) {
      setScore(prev => prev + 10)
      
      // Sonraki soruya geç
      if (currentQuestionIndex + 1 < questions.length) {
        setTimeout(() => {
          const nextIndex = currentQuestionIndex + 1
          setCurrentQuestionIndex(nextIndex)
          setSelectedAnswer('')
          setShowAnswerFeedback(false)
        }, 1500)
      } else {
        // Oyun tamamlandı
        setTimeout(() => {
          setGameCompleted(true)
          setShowLevelCompleteDialog(true)
          setShowAnswerFeedback(false)
        }, 1500)
      }
    } else {
      setLives(prev => prev - 1)
      setScore(prev => Math.max(0, prev - 5))
      
      // Can bitti mi kontrol et
      if (lives <= 1) {
        setTimeout(() => {
          setGameCompleted(true)
          setShowAnswerFeedback(false)
        }, 1500)
      } else {
        setTimeout(() => {
          setShowAnswerFeedback(false)
        }, 1500)
      }
    }

    setTimeout(() => {
      setGameLoading(false)
    }, 1500)
  }

  const completeLevel = async () => {
    setShowLevelCompleteDialog(false)
    
    try {
      const firestoreLevel = userLevel.toUpperCase()
      
      // /gameProgress/A1/quiz/{userId}/completedLevels/level_{currentLevel}
      const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'quiz', user.uid, 'completedLevels', `level_${currentLevel}`)
      
      await setDoc(levelDocRef, {
        completed: true,
        completedAt: new Date(),
        score: score
      })
      
      // Puanları da kaydet
      const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
      const userScoreDoc = await getDoc(userScoreRef)
      
      if (userScoreDoc.exists()) {
        const data = userScoreDoc.data()
        const completedLevels = data.completedLevels || []
        
        if (!completedLevels.includes(currentLevel)) {
          completedLevels.push(currentLevel)
        }
        
        await updateDoc(userScoreRef, {
          quizScore: increment(score),
          totalScore: increment(score),
          completedLevels: completedLevels
        })
      } else {
        await setDoc(userScoreRef, {
          username: userData?.username || user?.displayName || 'User',
          quizScore: score,
          totalScore: score,
          completedLevels: [currentLevel]
        })
      }
      
      
      
      console.log(`Quiz Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
      
      toast.success(`Quiz Level ${currentLevel} tamamlandı! Skorunuz: ${score}`)
      
      // Bir sonraki level'e geç
      const nextLevelNumber = currentLevel + 1
      onBack({ page: 'quiz', selectedLevel: nextLevelNumber })
    } catch (error) {
      console.error('Error saving score:', error)
      toast.error('Skor kaydedilirken hata oluştu')
      const nextLevelNumber = currentLevel + 1
      onBack({ page: 'quiz', selectedLevel: nextLevelNumber })
         }
   }

   

  const restartGame = () => {
    setCurrentQuestionIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setSelectedAnswer('')
    setShowAnswerFeedback(false)
    setIsAnswerCorrect(false)
  }

  const exitGame = () => {
    setShowExitDialog(true)
  }

  const confirmExit = () => {
    setShowExitDialog(false)
    onBack()
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={exitGame}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Quiz
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={exitGame}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quiz
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Level {currentLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Game Stats */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Skor: {score}
                </Typography>
                <Typography variant="h6">
                  Soru: {currentQuestionIndex + 1}/{questions.length}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {[1, 2, 3].map((heart) => (
                  <Box key={heart} sx={{ ml: 0.5 }}>
                    {heart <= lives ? (
                      <HeartIcon sx={{ color: 'red', fontSize: 24 }} />
                    ) : (
                      <HeartBorderIcon sx={{ color: 'gray', fontSize: 24 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Question Content */}
        <Card sx={{ mx: 2, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Soru {currentQuestionIndex + 1}:
              </Typography>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {currentQuestion?.questionText}
              </Typography>
            </Box>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={selectedAnswer}
                onChange={(e) => handleAnswerSelect(e.target.value)}
              >
                {[1, 2, 3, 4].map((optionNum) => {
                  const option = currentQuestion?.[`option${optionNum}`]
                  const isCorrectOption = option === currentQuestion?.[`option${currentQuestion?.correctOption}`]
                  const isSelected = selectedAnswer === option
                  
                  return option ? (
                    <FormControlLabel
                      key={optionNum}
                      value={option}
                      control={<Radio />}
                      label={option}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: showAnswerFeedback 
                          ? (isCorrectOption 
                              ? '2px solid #4caf50' 
                              : (isSelected && !isCorrectOption) 
                              ? '2px solid #f44336' 
                              : '1px solid #e0e0e0')
                          : '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: showAnswerFeedback 
                          ? (isCorrectOption 
                              ? '#e8f5e8' 
                              : (isSelected && !isCorrectOption) 
                              ? '#ffebee' 
                              : '#ffffff')
                          : '#ffffff',
                        color: showAnswerFeedback 
                          ? (isCorrectOption 
                              ? '#2e7d32' 
                              : (isSelected && !isCorrectOption) 
                              ? '#c62828' 
                              : '#000000')
                          : '#000000',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: showAnswerFeedback 
                            ? (isCorrectOption 
                                ? '#e8f5e8' 
                                : (isSelected && !isCorrectOption) 
                                ? '#ffebee' 
                                : '#f5f5f5')
                            : '#f5f5f5'
                        }
                      }}
                    />
                  ) : null
                })}
              </RadioGroup>
            </FormControl>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!selectedAnswer || gameCompleted}
                sx={{ minWidth: 120 }}
              >
                Cevabı Gönder
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Level Complete Dialog */}
        <Dialog open={showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            🏆 Quiz Level {currentLevel} Tamamlandı!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Final Skor: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Tebrikler! Quiz Level {currentLevel}'deki tüm soruları başarıyla cevapladınız.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={async () => {
                // Level'i ve puanları kaydet ve sonra geri dön
                try {
                  const firestoreLevel = userLevel.toUpperCase()
                  const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'quiz', user.uid, 'completedLevels', `level_${currentLevel}`)
                  
                  await setDoc(levelDocRef, {
                    completed: true,
                    completedAt: new Date(),
                    score: score
                  })
                  
                  // Puanları da kaydet
                  const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                  const userScoreDoc = await getDoc(userScoreRef)
                  
                  if (userScoreDoc.exists()) {
                    const data = userScoreDoc.data()
                    const completedLevels = data.completedLevels || []
                    
                    if (!completedLevels.includes(currentLevel)) {
                      completedLevels.push(currentLevel)
                    }
                    
                    await updateDoc(userScoreRef, {
                      quizScore: increment(score),
                      totalScore: increment(score),
                      completedLevels: completedLevels
                    })
                  } else {
                    await setDoc(userScoreRef, {
                      username: userData?.username || user?.displayName || 'User',
                      quizScore: score,
                      totalScore: score,
                      completedLevels: [currentLevel]
                    })
                  }
                  
                  console.log(`Quiz Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                  toast.success(`Quiz Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                } catch (error) {
                  console.error('Error saving score:', error)
                  toast.error('Skor kaydedilirken hata oluştu')
                }
                // Level seçim sayfasına dön
                onBack({ page: 'quiz-level-selection', userLevel: userLevel })
              }}
              sx={{ mr: 2 }}
            >
              Level Seçimi
            </Button>
            <Button 
              variant="contained" 
              onClick={completeLevel}
            >
              Sonraki Level
            </Button>
          </DialogActions>
        </Dialog>

        {/* Game Over Dialog */}
        <Dialog open={gameCompleted && !showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {lives > 0 ? '🎉 Quiz Tamamlandı!' : '😔 Quiz Bitti'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Skorunuz: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {lives > 0 
                  ? `Tebrikler! ${questions.length} soruyu başarıyla cevapladınız.`
                  : 'Canlarınız tükendi. Tekrar deneyin!'
                }
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={async () => {
                // Eğer oyun başarıyla tamamlandıysa level'i ve puanları kaydet
                if (lives > 0) {
                  try {
                    const firestoreLevel = userLevel.toUpperCase()
                    const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'quiz', user.uid, 'completedLevels', `level_${currentLevel}`)
                    
                    await setDoc(levelDocRef, {
                      completed: true,
                      completedAt: new Date(),
                      score: score
                    })
                    
                    // Puanları da kaydet
                    const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                    const userScoreDoc = await getDoc(userScoreRef)
                    
                    if (userScoreDoc.exists()) {
                      const data = userScoreDoc.data()
                      const completedLevels = data.completedLevels || []
                      
                      if (!completedLevels.includes(currentLevel)) {
                        completedLevels.push(currentLevel)
                      }
                      
                      await updateDoc(userScoreRef, {
                        quizScore: increment(score),
                        totalScore: increment(score),
                        completedLevels: completedLevels
                      })
                    } else {
                      await setDoc(userScoreRef, {
                        username: userData?.username || user?.displayName || 'User',
                        quizScore: score,
                        totalScore: score,
                        completedLevels: [currentLevel]
                      })
                    }
                    
                    console.log(`Quiz Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                    toast.success(`Quiz Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                  } catch (error) {
                    console.error('Error saving score:', error)
                    toast.error('Skor kaydedilirken hata oluştu')
                  }
                }
                // Level seçim sayfasına dön
                onBack({ page: 'quiz-level-selection', userLevel: userLevel })
              }}
              sx={{ mr: 2 }}
            >
              Level Seçimi
            </Button>
            <Button 
              variant="contained" 
              onClick={restartGame}
            >
              Tekrar Oyna
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exit Confirmation Dialog */}
        <Dialog open={showExitDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Oyundan Çık</DialogTitle>
          <DialogContent>
            <Typography>
              Oyundan çıkmak istediğinizden emin misiniz? İlerlemeniz kaydedilmeyecek.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExitDialog(false)}>
              İptal
            </Button>
            <Button onClick={confirmExit} color="error">
              Çık
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default Quiz
